import { useEffect, useReducer, useRef, useState } from "react";
import UserSelect, { UserOption } from "../../Inputs/UserSelect";
import { Button } from "../Buttons/Button";
import { Icon } from "../Icons/LucideIcons";
import { Body } from "../Typography";
import {
  CollaboratorSection,
  InfoRow,
  InlineLink,
  SearchInputWrapper,
  SearchRow,
  SearchSection,
  ShareFormBody,
} from "./__styles__/ShareForm";
import { CollaboratorList } from "./CollaboratorList";
import { InheritedPermissionRow } from "./InheritedPermissionRow";
import { Collaborator, CollaboratorAction, ShareFormProps } from "./types";

const updateCollaborator = (
  collaborator: Collaborator,
  updates: Partial<Pick<Collaborator, "permission" | "role" | "userGroupId">>,
): Collaborator => {
  switch (collaborator.entityType) {
    case "publicUser":
      return { ...collaborator, ...updates };
    case "publicUserInvite":
      return { ...collaborator, ...updates };
    case "emailInvite":
      return { ...collaborator, ...updates };
  }
};

const collaboratorReducer = (
  state: Collaborator[],
  action: CollaboratorAction,
): Collaborator[] => {
  switch (action.type) {
    case "ADD_COLLABORATOR":
      // Don't add if already exists
      if (state.some(c => c.id === action.payload.id)) {
        return state;
      }
      return [...state, action.payload];
    case "REMOVE_COLLABORATOR":
      return state.filter(c => c.id !== action.payload.id);
    case "UPDATE_PERMISSION":
      return state.map(c =>
        c.id === action.payload.id ?
          updateCollaborator(c, { permission: action.payload.permission })
        : c,
      );
    case "SET_COLLABORATORS":
      return action.payload;
    case "UPDATE_ROLE":
      return state.map(c =>
        c.id === action.payload.id ?
          updateCollaborator(c, {
            role: action.payload.role,
            userGroupId: action.payload.userGroupId,
          })
        : c,
      );
    default:
      return state;
  }
};

const buildCollaboratorFromUserOption = (
  userOption: UserOption,
  defaultPermission: string,
): Collaborator => {
  const name =
    userOption.firstName && userOption.lastName ?
      `${userOption.firstName} ${userOption.lastName}`
    : userOption.email;

  const base = {
    id: userOption.id,
    name,
    description: userOption.email,
    permission: defaultPermission,
    email: userOption.email,
  };

  if (userOption.isNewEmailInvite) {
    return {
      ...base,
      entityType: "emailInvite" as const,
      role: userOption.role?.name,
    };
  }

  if (userOption.isInvite && userOption.role.id) {
    return {
      ...base,
      entityType: "publicUserInvite" as const,
      status: "inviteSent",
      role: userOption.role.name,
      userGroupId: userOption.role.id,
    };
  }

  return {
    ...base,
    entityType: "publicUser" as const,
    status: userOption.status ?? "",
    role: userOption.role?.name,
    userGroupId: userOption.role?.id,
  };
};

export const ShareForm = ({
  searchPlaceholder = "Add email address",
  sectionLabel = "Who has access",
  infoText,
  initialCollaborators = [],
  permissionOptions,
  onStateChange,
  buttonText = "Add",
  onRemove,
  includeUsers = true,
  includePublicUsers = true,
  includeInvites = false,
  inheritedPermissions,
  onInheritedPermissionGroupClick,
  showPermissionOptions = true,
  roleOptions,
}: ShareFormProps) => {
  const [collaborators, dispatch] = useReducer(
    collaboratorReducer,
    initialCollaborators,
  );
  const [selectedUserObjects, setSelectedUserObjects] = useState<UserOption[]>(
    [],
  );
  const [userSelectKey, setUserSelectKey] = useState(0);

  const handleUserObjectsChange = (users: UserOption[]) => {
    const newEmailInvite = users.find(u => u.isNewEmailInvite);
    if (newEmailInvite) {
      const defaultPermission = permissionOptions[0]?.value ?? "can_view";
      dispatch({
        type: "ADD_COLLABORATOR",
        payload: {
          id: newEmailInvite.id,
          name: newEmailInvite.email,
          description: newEmailInvite.email,
          permission: defaultPermission,
          email: newEmailInvite.email,
          entityType: "emailInvite",
        },
      });
      setSelectedUserObjects([]);
      setUserSelectKey(prev => prev + 1);
      return;
    }
    setSelectedUserObjects(users);
  };

  useEffect(() => {
    onStateChange?.(collaborators);
  }, [collaborators, onStateChange]);

  // Track the serialized initial collaborators to detect actual changes
  const prevInitialCollaboratorsRef = useRef<string>("");
  useEffect(() => {
    const serialized = JSON.stringify(initialCollaborators);
    if (serialized !== prevInitialCollaboratorsRef.current) {
      prevInitialCollaboratorsRef.current = serialized;
      dispatch({ type: "SET_COLLABORATORS", payload: initialCollaborators });
    }
  }, [initialCollaborators]);

  const hasSelectedUser = selectedUserObjects.length > 0;

  const handleAddCollaborator = () => {
    if (!hasSelectedUser) return;

    const defaultPermission = permissionOptions[0]?.value ?? "can_view";

    selectedUserObjects.forEach(userOption => {
      dispatch({
        type: "ADD_COLLABORATOR",
        payload: buildCollaboratorFromUserOption(userOption, defaultPermission),
      });
    });

    // Reset selection by remounting UserSelect
    setSelectedUserObjects([]);
    setUserSelectKey(prev => prev + 1);
  };

  const handlePermissionChange = (
    collaboratorId: string,
    permission: string,
  ) => {
    dispatch({
      type: "UPDATE_PERMISSION",
      payload: { id: collaboratorId, permission },
    });
  };

  const handleRoleChange = (
    collaboratorId: string,
    role: string,
    userGroupId: string,
  ) => {
    dispatch({
      type: "UPDATE_ROLE",
      payload: { id: collaboratorId, role, userGroupId },
    });
  };

  const handleRemove = (collaboratorId: string) => {
    dispatch({
      type: "REMOVE_COLLABORATOR",
      payload: { id: collaboratorId },
    });
    onRemove?.(collaboratorId);
  };

  return (
    <ShareFormBody>
      <SearchSection>
        <SearchRow>
          <SearchInputWrapper>
            <UserSelect
              key={userSelectKey}
              name="shareUser"
              value={{ users: [], publicUsers: [] }}
              onChange={() => {}}
              onUserObjectsChange={handleUserObjectsChange}
              placeholder={searchPlaceholder}
              includeUsers={includeUsers}
              includePublicUsers={includePublicUsers}
              includeInvites={includeInvites}
              showSelectAll={false}
            />
          </SearchInputWrapper>
          <Button
            variant="primary"
            size="large"
            title={buttonText}
            onPress={handleAddCollaborator}
            isDisabled={!hasSelectedUser}
          />
        </SearchRow>
        {infoText && (
          <InfoRow>
            <Icon iconName="users" size={16} color="contentSecondary" />
            <Body type="regular" size="default" color="contentSecondary">
              {infoText.text}
              {infoText.linkText && infoText.linkHref && (
                <>
                  {" "}
                  <InlineLink
                    href={infoText.linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {infoText.linkText}
                  </InlineLink>
                </>
              )}
            </Body>
          </InfoRow>
        )}
      </SearchSection>

      {(collaborators.length > 0 ||
        (inheritedPermissions && inheritedPermissions.length > 0)) && (
        <CollaboratorSection>
          <Body type="emphasis" size="small" color="contentPrimary">
            {sectionLabel}
          </Body>
          {inheritedPermissions?.map(group => (
            <InheritedPermissionRow
              key={group.id}
              group={group}
              onClick={onInheritedPermissionGroupClick}
            />
          ))}
          <CollaboratorList
            collaborators={collaborators}
            permissionOptions={permissionOptions}
            onPermissionChange={handlePermissionChange}
            onRemove={handleRemove}
            showPermissionOptions={showPermissionOptions}
            roleOptions={roleOptions}
            onRoleChange={handleRoleChange}
          />
        </CollaboratorSection>
      )}
    </ShareFormBody>
  );
};
